import React, { useState } from 'react';
import { 
  Layers, 
  Calculator, 
  Grid, 
  CreditCard, 
  ShoppingCart, 
  Check,
  Trash2
} from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент для расчета необходимых материалов
 */
const MaterialCalculator = ({ 
  results, 
  formData,
  flooringTypes,
  wallMaterialTypes,
  ceilingTypes
}) => {
  // Локальное состояние для вычислений материалов
  const [wasteFactor, setWasteFactor] = useState({
    flooring: 5,
    walls: 10,
    ceiling: 5
  });
  
  const [materialList, setMaterialList] = useState([]);
  
  // Получаем выбранные типы материалов
  const selectedFlooring = flooringTypes.find(t => t.id === formData.flooringType) || {};
  const selectedWallMaterial = wallMaterialTypes.find(t => t.id === formData.wallMaterialType) || {};
  const selectedCeilingMaterial = ceilingTypes.find(t => t.id === formData.ceilingType) || {};
  
  // Функция расчета необходимого количества материалов с учетом запаса
  const calculateMaterialQuantity = (area, wastePercentage) => {
    return area * (1 + wastePercentage / 100);
  };
  
  // Функция расчета упаковок материала
  const calculatePackages = (totalArea, packageCoverage) => {
    if (!packageCoverage || packageCoverage <= 0) return 0;
    return Math.ceil(totalArea / packageCoverage);
  };
  
  // Рассчитываем количество материала для пола
  const floorAreaWithWaste = calculateMaterialQuantity(results.floorArea, wasteFactor.flooring);
  // Для ламината типичная упаковка покрывает примерно 2.5 м²
  const flooringPackages = calculatePackages(floorAreaWithWaste, 2.5);
  
  // Рассчитываем количество материала для стен
  const wallAreaWithWaste = calculateMaterialQuantity(results.wallArea, wasteFactor.walls);
  // Для обоев типичный рулон покрывает примерно 5 м²
  // Для краски типичная упаковка - 10 м²
  const wallPackageCoverage = selectedWallMaterial.id === 'wallpaper' ? 5 : 10;
  const wallPackages = calculatePackages(wallAreaWithWaste, wallPackageCoverage);
  
  // Рассчитываем количество материала для потолка
  const ceilingAreaWithWaste = calculateMaterialQuantity(results.ceilingArea, wasteFactor.ceiling);
  // Для натяжного потолка - целое число м²
  // Для краски типичная упаковка - 10 м²
  const ceilingPackageCoverage = selectedCeilingMaterial.id === 'stretch' ? 1 : 10;
  const ceilingPackages = calculatePackages(ceilingAreaWithWaste, ceilingPackageCoverage);
  
  // Фукция добавления материала в список
  const addToMaterialList = (type, name, quantity, unit, unitPrice) => {
    const newItem = {
      id: Date.now().toString(),
      type,
      name,
      quantity,
      unit,
      unitPrice,
      totalPrice: quantity * unitPrice
    };
    
    setMaterialList(prev => [...prev, newItem]);
  };
  
  // Удаление материала из списка
  const removeFromList = (id) => {
    setMaterialList(prev => prev.filter(item => item.id !== id));
  };
  
  // Общая стоимость материалов в списке
  const totalListCost = materialList.reduce((sum, item) => sum + item.totalPrice, 0);
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 mt-8 mb-8">
      <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-6 h-6 mr-2 text-fuchsia-500" />
        Расчет необходимых материалов
      </h3>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
        {/* Напольное покрытие */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
          <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2 mb-3">
            <Grid className="w-5 h-5 text-fuchsia-500" />
            Напольное покрытие
          </h4>
          
          <div className="mb-4">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Материал:</p>
            <p className="text-base font-medium text-gray-900 dark:text-white">{selectedFlooring.name}</p>
          </div>
          
          <div className="flex justify-between mb-3">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Площадь:</p>
              <p className="text-base font-medium text-gray-900 dark:text-white">{results.floorArea.toFixed(2)} м²</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Запас:</p>
              <div className="flex items-center">
                <input
                  type="number"
                  min="0"
                  max="30"
                  value={wasteFactor.flooring}
                  onChange={(e) => setWasteFactor(prev => ({ ...prev, flooring: Number(e.target.value) }))}
                  className="form-input w-16 text-center"
                />
                <span className="ml-1 text-gray-500 dark:text-gray-400">%</span>
              </div>
            </div>
          </div>
          
          <div className="flex justify-between mb-4">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">С запасом:</p>
              <p className="text-base font-medium text-gray-900 dark:text-white">{floorAreaWithWaste.toFixed(2)} м²</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Упаковок:</p>
              <p className="text-base font-medium text-gray-900 dark:text-white">{flooringPackages} шт.</p>
            </div>
          </div>
          
          <div className="mt-auto">
            <Button
              fullWidth
              variant="outline"
              size="sm"
              onClick={() => addToMaterialList(
                'flooring',
                selectedFlooring.name,
                flooringPackages,
                'уп.',
                parseFloat(formData.flooringUnitCost) * 2.5 // стоимость упаковки
              )}
              icon={<ShoppingCart className="w-4 h-4" />}
            >
              Добавить в список
            </Button>
          </div>
        </div>
        
        {/* Отделка стен */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
          <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2 mb-3">
            <Layers className="w-5 h-5 text-fuchsia-500" />
            Отделка стен
          </h4>
          
          <div className="mb-4">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Материал:</p>
            <p className="text-base font-medium text-gray-900 dark:text-white">{selectedWallMaterial.name}</p>
          </div>
          
          <div className="flex justify-between mb-3">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Площадь:</p>
              <p className="text-base font-medium text-gray-900 dark:text-white">{results.wallArea.toFixed(2)} м²</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Запас:</p>
              <div className="flex items-center">
                <input
                  type="number"
                  min="0"
                  max="30"
                  value={wasteFactor.walls}
                  onChange={(e) => setWasteFactor(prev => ({ ...prev, walls: Number(e.target.value) }))}
                  className="form-input w-16 text-center"
                />
                <span className="ml-1 text-gray-500 dark:text-gray-400">%</span>
              </div>
            </div>
          </div>
          
          <div className="flex justify-between mb-4">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">С запасом:</p>
              <p className="text-base font-medium text-gray-900 dark:text-white">{wallAreaWithWaste.toFixed(2)} м²</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                {selectedWallMaterial.id === 'wallpaper' ? 'Рулонов:' : 
                 selectedWallMaterial.id === 'paint' ? 'Банок:' : 
                 'Упаковок:'}
              </p>
              <p className="text-base font-medium text-gray-900 dark:text-white">{wallPackages} шт.</p>
            </div>
          </div>
          
          <div className="mt-auto">
            <Button
              fullWidth
              variant="outline"
              size="sm"
              onClick={() => addToMaterialList(
                'walls',
                selectedWallMaterial.name,
                wallPackages,
                selectedWallMaterial.id === 'wallpaper' ? 'рул.' : 
                selectedWallMaterial.id === 'paint' ? 'банок' : 'уп.',
                parseFloat(formData.wallMaterialUnitCost) * wallPackageCoverage
              )}
              icon={<ShoppingCart className="w-4 h-4" />}
            >
              Добавить в список
            </Button>
          </div>
        </div>
        
        {/* Отделка потолка */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
          <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2 mb-3">
            <Layers className="w-5 h-5 text-fuchsia-500" />
            Отделка потолка
          </h4>
          
          <div className="mb-4">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Материал:</p>
            <p className="text-base font-medium text-gray-900 dark:text-white">{selectedCeilingMaterial.name}</p>
          </div>
          
          <div className="flex justify-between mb-3">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Площадь:</p>
              <p className="text-base font-medium text-gray-900 dark:text-white">{results.ceilingArea.toFixed(2)} м²</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Запас:</p>
              <div className="flex items-center">
                <input
                  type="number"
                  min="0"
                  max="30"
                  value={wasteFactor.ceiling}
                  onChange={(e) => setWasteFactor(prev => ({ ...prev, ceiling: Number(e.target.value) }))}
                  className="form-input w-16 text-center"
                />
                <span className="ml-1 text-gray-500 dark:text-gray-400">%</span>
              </div>
            </div>
          </div>
          
          <div className="flex justify-between mb-4">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">С запасом:</p>
              <p className="text-base font-medium text-gray-900 dark:text-white">{ceilingAreaWithWaste.toFixed(2)} м²</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                {selectedCeilingMaterial.id === 'paint' ? 'Банок:' : 'Упаковок:'}
              </p>
              <p className="text-base font-medium text-gray-900 dark:text-white">{ceilingPackages} шт.</p>
            </div>
          </div>
          
          <div className="mt-auto">
            <Button
              fullWidth
              variant="outline"
              size="sm"
              onClick={() => addToMaterialList(
                'ceiling',
                selectedCeilingMaterial.name,
                ceilingPackages,
                selectedCeilingMaterial.id === 'paint' ? 'банок' : 'уп.',
                parseFloat(formData.ceilingUnitCost) * ceilingPackageCoverage
              )}
              icon={<ShoppingCart className="w-4 h-4" />}
            >
              Добавить в список
            </Button>
          </div>
        </div>
      </div>
      
      {/* Список материалов */}
      <div className="mt-8">
        <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2 mb-4">
          <ShoppingCart className="w-5 h-5 text-fuchsia-500" />
          Список материалов
        </h4>
        
        {materialList.length === 0 ? (
          <div className="text-center py-10 border border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
            <p className="text-gray-500 dark:text-gray-400">
              Добавьте материалы в список для составления сметы
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Материал
                  </th>
                  <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Количество
                  </th>
                  <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Цена за единицу
                  </th>
                  <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Стоимость
                  </th>
                  <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Действия
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {materialList.map((item) => (
                  <tr key={item.id}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{item.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {item.type === 'flooring' ? 'Напольное покрытие' : 
                         item.type === 'walls' ? 'Отделка стен' : 'Отделка потолка'}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <span className="text-sm text-gray-800 dark:text-gray-200">
                        {item.quantity} {item.unit}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <span className="text-sm text-gray-800 dark:text-gray-200">
                        {formatCurrency(item.unitPrice, 0)} ₽
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <span className="text-sm font-medium text-fuchsia-600 dark:text-fuchsia-400">
                        {formatCurrency(item.totalPrice, 0)} ₽
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <button
                        onClick={() => removeFromList(item.id)}
                        className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {/* Итоговая строка */}
                <tr className="bg-gray-50 dark:bg-gray-700">
                  <td colSpan="3" className="px-4 py-3 whitespace-nowrap text-right">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">ИТОГО:</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <span className="text-base font-bold text-fuchsia-700 dark:text-fuchsia-300">
                      {formatCurrency(totalListCost, 0)} ₽
                    </span>
                  </td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
        
        <div className="mt-6 p-4 bg-fuchsia-50 dark:bg-fuchsia-900/20 rounded-lg border border-fuchsia-100 dark:border-fuchsia-800">
          <div className="flex items-start">
            <div className="mt-1 mr-3 flex-shrink-0">
              <Check className="w-5 h-5 text-fuchsia-500" />
            </div>
            <div>
              <h5 className="text-sm font-medium text-fuchsia-700 dark:text-fuchsia-300 mb-1">
                Рекомендации по закупке материалов
              </h5>
              <p className="text-sm text-fuchsia-600 dark:text-fuchsia-400">
                При выборе материалов учитывайте особенности помещения. Для влажных помещений (кухня, ванная) 
                рекомендуются влагостойкие материалы. Для экономии рекомендуем сравнить цены в нескольких 
                строительных магазинах, а заказывать все материалы одной партией.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaterialCalculator;